import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatBigNum, formatCrowns } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import type {
  ActiveVocationDto,
  TradeListing,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

// Cleanup for the trade-picker's document listeners; torn down before each
// re-bind so repeated renders don't stack document listeners (previously only
// removed on Escape).
let tradePickerCleanup: (() => void) | null = null;

interface TierMeta {
  label: string;
  name: string;
  gradient: string;
}

/**
 * Tier display metadata, keyed by the trade's `tier` field (1-6). The server
 * sends an explicit `tier` on every TradeListing, so the client never infers
 * tier from array position — trades can be added anywhere without breaking
 * the chips. The gradient is the fallback chip swatch shown until real banner
 * art lands under /img/trades/<slug>.webp.
 */
const TIER_META: Record<number, TierMeta> = {
  1: { label: 'I',   name: 'Common Labour',
       gradient: 'linear-gradient(135deg, #e8d8a8 0%, #a08d5a 100%)' },
  2: { label: 'II',  name: 'Skilled Labour',
       gradient: 'linear-gradient(135deg, #c08a40 0%, #6a3a0e 100%)' },
  3: { label: 'III', name: 'Established Craft',
       gradient: 'linear-gradient(135deg, #a8a8b0 0%, #4a4a52 100%)' },
  4: { label: 'IV',  name: 'Senior Offices',
       gradient: 'linear-gradient(135deg, #f0d672 0%, #8a6614 100%)' },
  5: { label: 'V',   name: 'Court Titans',
       gradient: 'linear-gradient(135deg, #b22c2c 0%, #6b1f1f 100%)' },
  6: { label: 'VI',  name: 'Realm-Shapers',
       gradient: 'linear-gradient(135deg, #3a5aa8 0%, #1a2a5a 100%)' },
};

function tierMeta(tier: number): TierMeta {
  return TIER_META[tier] ?? TIER_META[1]!;
}

function bannerImageFor(slug: string): string {
  return `/img/trades/${slug}.webp`;
}

let countdownTimer: number | null = null;
let totalTrades = 0;

function clearCountdown(): void {
  if (countdownTimer !== null) {
    window.clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

function pickerChip(t: TradeListing, idx: number, locked: boolean): string {
  const tier = tierMeta(t.tier);
  const fallback = `<div class="trade-chip-grad" style="background:${tier.gradient}"><span class="trade-chip-roman">${tier.label}</span></div>`;
  return `
    <span class="trade-chip">
      <img
        class="trade-chip-img"
        src="${bannerImageFor(t.key)}"
        alt=""
        loading="lazy"
        data-fallback='${fallback.replace(/'/g, '&#39;')}'
        ${locked ? 'style="filter: grayscale(1) brightness(0.55);"' : ''}
      />
      <span class="trade-tier-num">${idx + 1}<span class="trade-tier-total">/${totalTrades}</span></span>
    </span>
  `;
}

export async function renderTrades(root: HTMLElement): Promise<void> {
  let trades: TradeListing[] = [];
  let active: ActiveVocationDto | null = null;
  let cycleSeconds = 0;

  try {
    const me = await api.me();
    store.set({ account: me.account, character: me.character });
    const cat = await api.listTrades();
    trades = cat.trades;
    cycleSeconds = cat.cycleSeconds;
    const status = await api.activeVocation();
    active = status.active;
  } catch {
    // tolerated
  }
  totalTrades = trades.length;

  let flash: FlashState | null = null;

  let selectedKey: string | null = (() => {
    if (active) return active.tradeKey;
    const char = store.get().character;
    if (char) {
      const eligible = trades.find(
        (t) =>
          char.toil >= t.requirements.toil &&
          char.wits >= t.requirements.wits &&
          char.endurance >= t.requirements.endurance,
      );
      if (eligible) return eligible.key;
    }
    return trades[0]?.key ?? null;
  })();

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      const selected = trades.find((t) => t.key === selectedKey) ?? null;
      const selectedIdx = selected ? trades.findIndex((t) => t.key === selected.key) : -1;
      const selectedTier = selected ? tierMeta(selected.tier) : null;

      const meetsReqs = (t: TradeListing) =>
        char.toil >= t.requirements.toil &&
        char.wits >= t.requirements.wits &&
        char.endurance >= t.requirements.endurance;

      const isCurrent = selected !== null && active?.tradeKey === selected.key;
      const previewMeets = selected ? meetsReqs(selected) : false;

      // Requirement rows for the shared Requires/Reward table — unmet stats
      // show in red (warn).
      const reqRows = (t: TradeListing): string[] => {
        const rows: string[] = [];
        if (t.requirements.toil > 0)
          rows.push(cgRow('Toil', formatBigNum(t.requirements.toil), char.toil >= t.requirements.toil ? '' : 'warn'));
        if (t.requirements.wits > 0)
          rows.push(cgRow('Wits', formatBigNum(t.requirements.wits), char.wits >= t.requirements.wits ? '' : 'warn'));
        if (t.requirements.endurance > 0)
          rows.push(cgRow('Endurance', formatBigNum(t.requirements.endurance), char.endurance >= t.requirements.endurance ? '' : 'warn'));
        return rows;
      };

      const gainRows = (t: TradeListing): string[] => {
        const rows: string[] = [];
        if (t.gains.toil) rows.push(cgRow('Toil', `+${formatBigNum(t.gains.toil)}`, 'gain'));
        if (t.gains.wits) rows.push(cgRow('Wits', `+${formatBigNum(t.gains.wits)}`, 'gain'));
        if (t.gains.endurance) rows.push(cgRow('Endurance', `+${formatBigNum(t.gains.endurance)}`, 'gain'));
        return rows;
      };

      // ── Ledger panel — the player's working stats, trade income, and progression ──
      const renderLedger = (): string => {
        // Highest-tier trade the player currently qualifies for (trades array is
        // tier-ascending, so the last qualifying entry wins).
        let highestIdx = -1;
        trades.forEach((t, i) => {
          if (meetsReqs(t)) highestIdx = i;
        });
        const highest = highestIdx >= 0 ? trades[highestIdx] : null;
        const highestTier = highest ? tierMeta(highest.tier) : null;
        const unlockedCount = trades.filter(meetsReqs).length;

        // Current-trade derived stats.
        const currentTrade = active ? trades.find((t) => t.key === active!.tradeKey) ?? null : null;
        const daysWorking = active
          ? Math.max(0, Math.floor((Date.now() - Date.parse(active.joinedAt)) / 86_400_000))
          : 0;

        const statBlock = (label: string, value: string, gold = false): string => `
          <div class="ledger-stat">
            <span class="ledger-label">${label}</span>
            <span class="ledger-value${gold ? ' gold' : ''}">${value}</span>
          </div>`;

        return `
          <div class="panel ledger-panel">
            <h2 style="margin-top:0;">Thy Ledger</h2>
            <div class="ledger-grid">
              ${statBlock('Toil', formatBigNum(char.toil))}
              ${statBlock('Wits', formatBigNum(char.wits))}
              ${statBlock('Endurance', formatBigNum(char.endurance))}
              ${statBlock(
                'Trade earnings',
                active ? `${formatCrowns(active.totalEarned)} crowns` : '—',
                true,
              )}
              ${statBlock('Vocations unlocked', `${unlockedCount} / ${trades.length}`)}
              ${statBlock(
                'Highest thou qualify for',
                highest && highestTier
                  ? `${escapeHtml(highest.name)} <span class="ledger-sub">Tier ${highestTier.label}</span>`
                  : 'None yet',
              )}
              ${
                active && currentTrade
                  ? statBlock('Days in current trade', String(daysWorking)) +
                    statBlock(
                      'Current daily wage',
                      `${formatCrowns(currentTrade.effectiveWage)} crowns`,
                      true,
                    ) +
                    statBlock(
                      'Wage multiplier',
                      currentTrade.wageMultiplier > 1.01
                        ? `×${currentTrade.wageMultiplier.toFixed(2)} <span class="ledger-sub">stats over the bar</span>`
                        : '×1.00 <span class="ledger-sub">at the requirement</span>',
                    )
                  : ''
              }
            </div>
            ${
              !active
                ? `<p style="font-style:italic; color: var(--c-ink-soft); margin: var(--s-3) 0 0 0;">
                     Thou holdest no trade. Pick one below to start earning — trade earnings tally from the day thou join.
                   </p>`
                : ''
            }
          </div>
        `;
      };

      const renderFeaturedCard = (
        t: TradeListing,
        tier: TierMeta,
        viewing: boolean,
        meets: boolean,
        cur: ActiveVocationDto | null,
      ): string => {
        const switchingFromCurrent = !!(cur && cur.tradeKey !== t.key);
        const buttonHtml = viewing
          ? `<button class="btn ghost" id="leave-trade">Leave this trade</button>`
          : meets
          ? `<button class="btn" id="take-trade">${switchingFromCurrent ? 'Switch to this trade' : 'Take the trade'}</button>`
          : `<button class="btn" disabled>Requirements not met</button>`;

        // Next-payment countdown only — career earnings & tenure live in the
        // Ledger panel at the top of the page (Trade earnings / Days in current
        // trade), so we don't repeat them here.
        const timerBlock = viewing && cur
          ? `<div class="trade-feature-block">
               <h4>Next payment</h4>
               <p class="timer-line">
                 ${cur.ready
                   ? `<strong>Ready — paid on next visit</strong>`
                   : `in <span id="cd">${formatRemaining(cur.nextClaimAt)}</span>`}
               </p>
             </div>`
          : '';

        const fallbackGradient = `<div class="trade-feature-banner-grad" style="background:${tier.gradient}"><span class="roman">${tier.label}</span></div>`;

        return `
          <div class="trade-feature">
            <figure class="trade-feature-banner">
              <img
                class="trade-feature-banner-img"
                src="${bannerImageFor(t.key)}"
                alt="${escapeHtml(t.name)}"
                loading="lazy"
                data-fallback='${fallbackGradient.replace(/'/g, '&#39;')}'
              />
              <figcaption class="trade-feature-caption">
                <div class="trade-feature-tier">Tier ${tier.label} — ${tier.name}</div>
                <div class="trade-feature-name">${escapeHtml(t.name)}</div>
                <p class="trade-feature-blurb">"${escapeHtml(t.blurb)}"</p>
              </figcaption>
            </figure>

            <div class="trade-feature-body">
              <div class="trade-feature-block">
                <h4>Wage</h4>
                <div class="wage-big">${formatCrowns(t.effectiveWage)} crowns / day</div>
                <div class="wage-scale">
                  base ${formatCrowns(t.wageCrowns)} ${t.wageMultiplier > 1.01 ? `× ${t.wageMultiplier.toFixed(2)} (working stats above the bar)` : ''}
                </div>
              </div>

              ${cgTable([
                cgCol('Requires', reqRows(t).length ? reqRows(t) : [cgRow('—', 'None')]),
                cgCol('Stat gains · per cycle', gainRows(t).length ? gainRows(t) : [cgRow('—', 'None')]),
              ])}

              ${timerBlock}
            </div>

            <div class="trade-feature-actions">
              ${buttonHtml}
              ${!viewing && cur && switchingFromCurrent
                ? `<span class="trade-switch-warn">Currently working: <strong>${escapeHtml(cur.tradeName)}</strong> — switching resets thy wage timer.</span>`
                : ''}
              ${!viewing && !cur
                ? `<span class="trade-switch-warn">No trade chosen yet — claim this one to start earning.</span>`
                : ''}
            </div>
          </div>
        `;
      };

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/trades.webp" alt="" />
        <h1>Trades</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          Honest work. The kind nobody talks about over wine.
        </p>

        <div id="flash"></div>

        ${renderLedger()}

        <div class="panel">
          <h2 style="margin-top:0;">
            ${active ? `Currently working — <span style="color: var(--c-oxblood);">${escapeHtml(active.tradeName)}</span>` : 'Pick a trade'}
            <span style="font-size:0.8rem; color: var(--c-ink-soft); font-weight:400;"> — ${trades.length} vocations</span>
          </h2>
          <p style="font-style:italic; color: var(--c-ink-soft); margin-top: 0;">
            Wages and stat gains are paid daily and collected automatically each time thou enter the realm.
            Higher-tier trades pay more — and reward those whose working stats exceed the trade's minima
            (a gentle sqrt curve, so quadrupling the bottleneck stat doubles thy wage).
          </p>

          <div class="field" style="margin-top: var(--s-3);">
            <label>Active vocation</label>
            ${selected ? `
              <div class="trade-picker" data-open="false">
                <button class="trade-picker-trigger" type="button" id="trade-trigger" aria-haspopup="listbox" aria-expanded="false">
                  ${pickerChip(selected, selectedIdx, false)}
                  <span class="trade-picker-body">
                    <span class="trade-picker-name">${escapeHtml(selected.name)}</span>
                    <span class="trade-picker-meta">
                      ${selectedTier ? `Tier ${selectedTier.label} — ${selectedTier.name}` : ''}
                      ${isCurrent ? '· <span style="color: var(--c-oxblood); font-weight:600;">currently working</span>' : ''}
                    </span>
                  </span>
                  <span class="trade-picker-caret">▾</span>
                </button>
                <div class="trade-picker-menu" role="listbox" aria-labelledby="trade-trigger">
                  ${trades.map((t, i) => {
                    const isSel = t.key === selectedKey;
                    const isCur = t.key === active?.tradeKey;
                    const locked = !meetsReqs(t);
                    const tier = tierMeta(t.tier);
                    return `
                      <button
                        class="trade-option ${isSel ? 'selected' : ''} ${locked ? 'locked' : ''} ${isCur ? 'current' : ''}"
                        type="button"
                        role="option"
                        data-key="${t.key}"
                        aria-selected="${isSel}"
                      >
                        ${pickerChip(t, i, locked)}
                        <span class="trade-option-body">
                          <span class="trade-option-name">${escapeHtml(t.name)}</span>
                          <span class="trade-option-meta">
                            <span class="tier-tag">Tier ${tier.label}</span>
                            <span class="wage-tag">${formatCrowns(t.effectiveWage)} crowns / day</span>
                            ${locked ? '<span class="locked-tag">requirements not met</span>' : ''}
                            ${isCur ? '<span class="current-tag">currently working</span>' : ''}
                          </span>
                        </span>
                        ${isSel ? '<span class="trade-check">✓</span>' : locked ? '<span class="trade-lock">⚿</span>' : ''}
                      </button>
                    `;
                  }).join('')}
                </div>
              </div>
            ` : `<p class="alert">No trades available. Try refreshing the page.</p>`}
          </div>

          ${selected && selectedTier ? renderFeaturedCard(selected, selectedTier, isCurrent, previewMeets, active) : ''}
        </div>

        <p id="cycle-hint" style="font-style:italic; color: var(--c-ink-soft); margin-top: var(--s-3); text-align: center;">
          Wages settle every ${formatDuration(cycleSeconds)}. One cycle is paid per visit — the realm does not hoard.
        </p>
      `;

      const style = document.createElement('style');
      style.textContent = TRADES_CSS;
      main.appendChild(style);

      // Picker wiring
      const picker = main.querySelector<HTMLDivElement>('.trade-picker');
      const trigger = main.querySelector<HTMLButtonElement>('#trade-trigger');
      if (picker && trigger) {
        const setOpen = (v: boolean) => {
          picker.setAttribute('data-open', String(v));
          trigger.setAttribute('aria-expanded', String(v));
        };
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          setOpen(picker.getAttribute('data-open') !== 'true');
        });
        picker.querySelectorAll<HTMLButtonElement>('.trade-option').forEach((opt) => {
          opt.addEventListener('click', (e) => {
            e.stopPropagation();
            const key = opt.dataset.key;
            if (!key) return;
            selectedKey = key;
            setOpen(false);
            anchored(renderMain);
          });
        });
        // Tear down listeners from a previous render before re-binding, so
        // repeated renders can't stack document listeners.
        tradePickerCleanup?.();
        const onDocClick = (ev: MouseEvent) => {
          if (!picker.contains(ev.target as Node)) setOpen(false);
        };
        const onKey = (ev: KeyboardEvent) => {
          if (ev.key === 'Escape') { setOpen(false); tradePickerCleanup?.(); }
        };
        document.addEventListener('click', onDocClick, { once: false });
        document.addEventListener('keydown', onKey);
        tradePickerCleanup = () => {
          document.removeEventListener('click', onDocClick);
          document.removeEventListener('keydown', onKey);
          tradePickerCleanup = null;
        };
      }

      // Flash
      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // Banner image fallback to gradient if /img/trades/<slug>.webp is missing
      main.querySelectorAll<HTMLImageElement>('img[data-fallback]').forEach((img) => {
        img.addEventListener('error', () => {
          const fb = img.dataset.fallback;
          const parent = img.parentElement;
          if (!fb || !parent) return;
          img.remove();
          parent.insertAdjacentHTML('afterbegin', fb);
        });
      });

      // Take / leave buttons
      const takeBtn = main.querySelector<HTMLButtonElement>('#take-trade');
      takeBtn?.addEventListener('click', async () => {
        if (!selected) return;
        if (active && active.tradeKey !== selected.key) {
          const ok = await showConfirmDialog({
            title: 'Change thy trade?',
            message: `Leave ${active.tradeName} and take ${selected.name}?\n\nThy daily wage timer will reset.`,
            confirmLabel: 'Change trade',
          });
          if (!ok) return;
        }
        takeBtn.disabled = true;
        try {
          const r = await api.joinTrade({ tradeKey: selected.key });
          store.set({ character: r.character });
          flash = {
            kind: 'success',
            message: `Taken: ${r.tradeName}. First wages available ${new Date(r.nextClaimAt).toLocaleString()}.`,
          };
          active = (await api.activeVocation()).active;
        } catch (err) {
          const msg = err instanceof HttpError ? err.message : 'Could not take trade.';
          flash = { kind: 'danger', message: msg };
        } finally {
          anchored(renderMain);
        }
      });

      const leaveBtn = main.querySelector<HTMLButtonElement>('#leave-trade');
      leaveBtn?.addEventListener('click', async () => {
        const okLeave = await showConfirmDialog({
          title: 'Leave thy trade?',
          message: 'Leave thy current trade? Thou canst rejoin or pick a different one.',
          confirmLabel: 'Leave trade',
        });
        if (!okLeave) return;
        leaveBtn.disabled = true;
        try {
          const r = await api.leaveTrade();
          store.set({ character: r.character });
          active = null;
          flash = { kind: 'success', message: 'Trade left. Pick a new one when ready.' };
        } catch (err) {
          const msg = err instanceof HttpError ? err.message : 'Leave failed.';
          flash = { kind: 'danger', message: msg };
        } finally {
          anchored(renderMain);
        }
      });

      // Live countdown for the currently-working trade
      clearCountdown();
      if (isCurrent && active && !active.ready) {
        countdownTimer = window.setInterval(async () => {
          const cd = main.querySelector<HTMLSpanElement>('#cd');
          if (!cd) {
            clearCountdown();
            return;
          }
          const remaining = formatRemaining(active!.nextClaimAt);
          cd.textContent = remaining;
          if (remaining === '0s') {
            clearCountdown();
            active = (await api.activeVocation()).active;
            anchored(renderMain);
          }
        }, 1000);
      }
    };

    anchored(renderMain);
  });

  window.addEventListener('hashchange', clearCountdown, { once: true });
}

function formatRemaining(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return '0s';
  const sec = Math.ceil(ms / 1000);
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} day${seconds >= 172800 ? 's' : ''}`;
}

const TRADES_CSS = `
.trade-picker { position: relative; max-width: 640px; }
.trade-picker-trigger {
  display: flex; align-items: center; gap: var(--s-3); width: 100%; padding: 8px 12px;
  background: linear-gradient(to bottom, rgba(255, 245, 220, 0.85), rgba(214, 195, 147, 0.6));
  border: 1px solid var(--c-rule-strong); border-radius: var(--radius);
  /* The gradient cell stays light-cream in both themes, so pin dark ink —
     var(--c-ink) flips to cream in dark mode and vanished on this cell. */
  cursor: pointer; font: inherit; color: #1c1108; text-align: left;
  box-shadow: var(--emboss-light), 0 1px 2px rgba(28, 17, 8, 0.10);
  transition: filter 0.12s ease, box-shadow 0.12s ease;
}
.trade-picker-trigger:hover { filter: brightness(1.04); }
.trade-picker[data-open="true"] .trade-picker-trigger {
  box-shadow: var(--emboss-light), 0 0 0 3px rgba(212, 169, 60, 0.30);
}
.trade-chip {
  flex: 0 0 auto; width: 80px; height: 40px; border-radius: 4px;
  border: 1px solid var(--c-rule-strong);
  box-shadow:
    inset 0 1px 0 rgba(255, 245, 220, 0.35),
    inset 0 -1px 0 rgba(28, 17, 8, 0.30),
    0 1px 1px rgba(28, 17, 8, 0.20);
  overflow: hidden; position: relative;
}
.trade-chip-img { display: block; width: 100%; height: 100%; object-fit: cover; image-rendering: -webkit-optimize-contrast; }
.trade-chip-grad { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.trade-chip-roman {
  font-family: var(--font-display); font-weight: 700; font-size: 1.1rem;
  color: rgba(255, 245, 220, 0.95); text-shadow: 0 1px 2px rgba(0, 0, 0, 0.6);
  letter-spacing: 0.04em;
}
.trade-tier-num {
  position: absolute; top: 2px; left: 2px; padding: 1px 5px;
  background: rgba(20, 12, 4, 0.78); color: #fff5e1;
  border: 1px solid rgba(255, 245, 220, 0.25); border-radius: 3px;
  font-family: var(--font-numeric); font-size: 0.72rem; font-weight: 700;
  line-height: 1; pointer-events: none; user-select: none;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.6);
}
.trade-tier-total { opacity: 0.65; font-weight: 400; margin-left: 1px; }
.trade-option.locked .trade-chip-img,
.trade-option.locked .trade-chip-grad { filter: grayscale(1) brightness(0.55); }
.trade-option.selected .trade-chip {
  border-color: var(--c-gold);
  box-shadow:
    inset 0 1px 0 rgba(255, 245, 220, 0.35),
    inset 0 -1px 0 rgba(28, 17, 8, 0.30),
    0 0 0 2px rgba(212, 169, 60, 0.55);
}
.trade-picker-body { display: flex; flex-direction: column; gap: 2px; flex: 1 1 auto; min-width: 0; }
/* Name + meta sit ON the trigger's light gradient — pin dark ink so they
   survive dark mode (the ink tokens flip to cream there). */
.trade-picker-name {
  font-family: var(--font-display); font-size: 1.1rem; font-weight: 700;
  letter-spacing: 0.03em; color: #1c1108;
}
.trade-picker-meta { font-family: var(--font-body); font-size: 0.9rem; color: rgba(28, 17, 8, 0.7); }
.trade-picker-caret { font-size: 1.2rem; color: var(--c-rule-strong); transition: transform 0.18s ease; }
.trade-picker[data-open="true"] .trade-picker-caret { transform: rotate(180deg); }

.trade-picker-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  max-height: 420px; overflow-y: auto;
  background: var(--c-parchment); background-image: var(--parchment-texture);
  border: 1px solid var(--c-rule-strong); border-radius: var(--radius);
  box-shadow: 0 0 0 1px rgba(212, 169, 60, 0.45), 0 12px 28px rgba(28, 17, 8, 0.35);
  z-index: 30; display: none; padding: 4px;
}
.trade-picker[data-open="true"] .trade-picker-menu { display: block; }

.trade-option {
  display: flex; align-items: center; gap: var(--s-3); width: 100%;
  padding: 8px 10px; background: transparent; border: 1px solid transparent;
  border-radius: var(--radius); cursor: pointer; font: inherit;
  color: var(--c-ink); text-align: left;
  transition: background 0.12s ease, border-color 0.12s ease;
}
.trade-option:hover { background: rgba(212, 169, 60, 0.16); border-color: var(--c-gold); }
.trade-option.selected { background: rgba(107, 31, 31, 0.10); border-color: var(--c-oxblood); }
.trade-option.current { background: rgba(212, 169, 60, 0.10); }
.trade-option.locked { opacity: 0.65; }

.trade-option-body { display: flex; flex-direction: column; gap: 2px; flex: 1 1 auto; min-width: 0; }
.trade-option-name {
  font-family: var(--font-display); font-size: 1rem; font-weight: 600;
  letter-spacing: 0.03em; color: var(--c-ink);
}
.trade-option-meta {
  font-family: var(--font-body); font-size: 0.85rem; color: var(--c-ink-soft);
  display: flex; gap: var(--s-2); flex-wrap: wrap;
}
.trade-option-meta .tier-tag { color: var(--c-ink-soft); font-style: italic; }
.trade-option-meta .wage-tag { color: var(--c-gold-text); font-weight: 700; font-family: var(--font-numeric); }
.trade-option-meta .locked-tag { color: var(--c-danger); font-style: italic; }
.trade-option-meta .current-tag { color: var(--c-oxblood); font-weight: 600; }
.trade-check { color: var(--c-oxblood); font-size: 1.1rem; font-weight: 700; }
.trade-lock { color: var(--c-rule-strong); }

.trade-feature { margin-top: var(--s-4); display: grid; gap: var(--s-3); }
.trade-feature-banner {
  position: relative; border-radius: var(--radius);
  border: 1px solid var(--c-rule-strong); overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(255, 245, 220, 0.20), 0 2px 6px rgba(28, 17, 8, 0.25);
  aspect-ratio: 4 / 1;
}
.trade-feature-banner-img,
.trade-feature-banner-grad {
  display: block; width: 100%; height: 100%; object-fit: cover;
  image-rendering: -webkit-optimize-contrast;
}
.trade-feature-banner-grad { display: flex; align-items: center; justify-content: center; }
.trade-feature-banner-grad .roman {
  font-family: var(--font-display); font-weight: 700; font-size: 4rem;
  color: rgba(255, 245, 220, 0.40); text-shadow: 0 2px 6px rgba(0, 0, 0, 0.55);
  letter-spacing: 0.06em;
}
.trade-feature-caption {
  position: absolute; left: 0; right: 0; bottom: 0;
  padding: var(--s-3) var(--s-4); color: #fff5e1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.70);
  background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.35) 60%, transparent 100%);
}
.trade-feature-name {
  font-family: var(--font-heading); font-weight: 700; font-size: 1.3rem;
  letter-spacing: 0.07em; text-transform: uppercase;
}
.trade-feature-tier { opacity: 0.85; font-size: 0.8rem; letter-spacing: 0.12em; text-transform: uppercase; }
.trade-feature-blurb {
  margin: 4px 0 0 0; font-family: var(--font-elegant); font-style: italic;
  font-size: 0.95rem; opacity: 0.95;
}
.trade-feature-body {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--s-3);
}
.trade-feature-block h4 {
  margin: 0 0 var(--s-1) 0; font-family: var(--font-display); font-size: 0.92rem;
  letter-spacing: 0.08em; text-transform: uppercase; color: var(--c-ink-soft);
}
.trade-feature-block .row { display: flex; flex-wrap: wrap; gap: var(--s-2); align-items: center; }
.trade-feature-block .wage-big {
  font-family: var(--font-numeric); font-size: 1.4rem;
  color: var(--c-gold-text); font-weight: 700;
}
.trade-feature-block .wage-scale { color: var(--c-ink-soft); font-size: 0.85rem; font-style: italic; }
.trade-feature-block .timer-line { font-family: var(--font-numeric); color: var(--c-ink); }
.trade-feature-block .timer-line strong { color: var(--c-oxblood); }

.trade-feature-actions {
  display: flex; flex-wrap: wrap; gap: var(--s-3);
  margin-top: var(--s-2); align-items: center;
}
.trade-switch-warn { font-style: italic; color: var(--c-ink-soft); font-size: 0.9rem; }

.pill.req-met { border-color: var(--c-success); color: var(--c-success); }
.pill.req-unmet { border-color: var(--c-danger); color: var(--c-danger); }
.pill.gold { border-color: var(--c-gold); color: var(--c-gold-text); font-weight: 700; }

/* ── Ledger panel ────────────────────────────────────────────────── */
.ledger-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--s-2);
  margin-top: var(--s-2);
}
.ledger-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--s-2) var(--s-3);
  background: var(--c-parchment);
  border: 1px solid var(--c-rule-soft);
  border-radius: var(--radius);
}
.ledger-label {
  font-family: var(--font-body);
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c-ink-soft);
}
.ledger-value {
  font-family: var(--font-numeric);
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--c-ink);
}
.ledger-value.gold { color: var(--c-gold-text); }
.ledger-sub {
  display: block;
  font-family: var(--font-body);
  font-size: 0.72rem;
  font-weight: 400;
  font-style: italic;
  color: var(--c-ink-soft);
  text-transform: none;
  letter-spacing: 0;
}

@media (max-width: 720px) {
  .trade-feature-banner { aspect-ratio: 16 / 9; }
  .trade-feature-name { font-size: 1.05rem; }
  .trade-feature-blurb { font-size: 0.85rem; }
  .trade-feature-banner-grad .roman { font-size: 2.6rem; }
}
`;
